import React, {
  FunctionComponent,
  ReactNode,
  RefObject,
  useState,
} from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';

import { dimmerAnimation } from '../../animation/dimmerAnimation';
import {
  safeFormattedIntlString,
  SafeFormattedMessage,
  SafeFormattedMessageWithoutSpread,
} from '../../formatters';
import { useIsNewUiEnabled } from '../../hooks/useIsNewUiEnabled';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LoaderIcon,
} from '../../protons/icons';
import SearchInput from '../SearchInput/SearchInput';

import useMenu from './Menu.hooks';
import {
  $Description,
  $MenuBack,
  $MenuButton,
  $MenuButtonContainer,
  $MenuContent,
  $MenuItem,
  $MenuItemStyled,
  $MenuLink,
  $MenuSubMenu,
  $MenuTextContainer,
  $MenuTitle,
} from './Menu.styles';
import { MENU_ITEM_TYPES, MenuItem } from './Menu.types';
import messages from './messages';

interface VerticalMenuitem {
  menuItems: MenuItem[];
  onClose?: () => void;
  dataTestId?: string;
  wrapperRef?: RefObject<any>;
  wrapperRefs?: RefObject<any>[];
  minWidth?: number;
}
const VerticalMenuContent: FunctionComponent<
  React.PropsWithChildren<VerticalMenuitem>
> = ({
  menuItems = [],
  onClose = () => undefined,
  dataTestId,
  wrapperRef,
  wrapperRefs,
  minWidth,
}) => {
  const { list } = useMenu({
    onClose,
    menuItems,
    wrapperRef,
    wrapperRefs,
  });
  const theme = useTheme();
  const isNewUiEnabled = useIsNewUiEnabled();
  const [searchQuery, setSearchQuery] = useState<string>();

  const filteredList = list.filter((item) => {
    if (!searchQuery) {
      return true;
    }

    if (
      item.type === MENU_ITEM_TYPES.LINK ||
      item.type === MENU_ITEM_TYPES.MENU ||
      item.type === MENU_ITEM_TYPES.BUTTON
    ) {
      return safeFormattedIntlString(item.label)
        ?.toLowerCase()
        ?.includes(searchQuery?.toLowerCase());
    }

    return true;
  });

  return filteredList.map((menuItem) => {
    let component: ReactNode;

    switch (menuItem.type) {
      case MENU_ITEM_TYPES.CUSTOM:
        component = React.cloneElement(menuItem.children, { onClose });
        break;

      case MENU_ITEM_TYPES.TITLE:
        component = (
          <$MenuTitle
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.TITLE}`
                : `menu_child_${MENU_ITEM_TYPES.TITLE}`
            }
            ref={menuItem.ref}
            $isNewUiEnabled={isNewUiEnabled}
          >
            <$MenuContent>
              {menuItem.isLoading && (
                <LoaderIcon
                  size="32px"
                  color={
                    isNewUiEnabled
                      ? theme.colors.mainTextColor
                      : theme.colors.white
                  }
                />
              )}
              {SafeFormattedMessage(menuItem.label)}
            </$MenuContent>
          </$MenuTitle>
        );
        break;

      case MENU_ITEM_TYPES.LINK:
        component = (
          <$MenuLink
            onClick={onClose}
            href={menuItem.href}
            ref={menuItem.ref}
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.LINK}`
                : `menu_child_${MENU_ITEM_TYPES.LINK}`
            }
            $color={menuItem.color}
            $isDisabled={menuItem.isDisabled}
            target={menuItem.target}
            $isNewUiEnabled={isNewUiEnabled}
          >
            <$MenuContent>
              {menuItem.isLoading ? (
                <LoaderIcon size="32px" color={theme.colors.white} />
              ) : (
                menuItem.icon
              )}
              {SafeFormattedMessage(menuItem.label)}
            </$MenuContent>
          </$MenuLink>
        );
        break;

      case MENU_ITEM_TYPES.MENU:
        component = (
          <$MenuSubMenu
            onClick={menuItem.onClick}
            type={BUTTON_TYPES.BUTTON}
            disabled={menuItem.isDisabled}
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.MENU}`
                : `menu_child_${MENU_ITEM_TYPES.MENU}`
            }
            $color={menuItem.color}
            $isNewUiEnabled={isNewUiEnabled}
          >
            {menuItem.isLoading && (
              <LoaderIcon size="20px" color={theme.colors.white} />
            )}
            {!menuItem.isLoading && menuItem.icon}

            <$MenuContent>{SafeFormattedMessage(menuItem.label)}</$MenuContent>

            <ChevronRightIcon size="20px" color={theme.colors.white} />
          </$MenuSubMenu>
        );
        break;

      case MENU_ITEM_TYPES.BACK:
        component = (
          <$MenuBack
            onClick={(e) => {
              setSearchQuery('');
              menuItem.onClick(e);
            }}
            type={BUTTON_TYPES.BUTTON}
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.BACK}`
                : `menu_child_${MENU_ITEM_TYPES.BACK}`
            }
            $isNewUiEnabled={isNewUiEnabled}
          >
            <ChevronLeftIcon size="20px" />

            <$MenuContent>
              <SafeFormattedMessageWithoutSpread message={messages.back} />
            </$MenuContent>
          </$MenuBack>
        );
        break;

      case MENU_ITEM_TYPES.SEARCH:
        component = (
          <SearchInput
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClear={() => setSearchQuery('')}
          />
        );
        break;

      default: {
        if (menuItem.CustomLabel || menuItem.PrefixAction) {
          component = (
            <$MenuButtonContainer
              $isNewUiEnabled={isNewUiEnabled}
              $color={menuItem.color}
              $isActive={menuItem.isActive}
              {...dimmerAnimation}
            >
              <$MenuItem
                onClick={(e: any) => {
                  const response = { ...menuItem };

                  if (menuItem.onClick) {
                    menuItem.onClick(response, e);
                  }
                }}
                data-testid={menuItem.dataTestId || 'menu_child_button'}
                disabled={menuItem.isDisabled}
                $color={menuItem.color}
                $isNewUiEnabled={isNewUiEnabled}
                $hasSecondaryAction={!!menuItem.SecondaryAction}
              >
                {!!menuItem.PrefixAction && menuItem.PrefixAction}

                {menuItem.isLoading && (
                  <LoaderIcon
                    size="32px"
                    color={
                      isNewUiEnabled
                        ? theme.colors.mainTextColor
                        : theme.colors.white
                    }
                  />
                )}
                {!menuItem.isLoading && menuItem.icon}

                <$MenuContent>
                  <$MenuTextContainer>
                    {!!menuItem.CustomLabel && (
                      <div onClick={(e) => e.stopPropagation()}>
                        {menuItem.CustomLabel}
                      </div>
                    )}
                    {!menuItem.CustomLabel &&
                      SafeFormattedMessage(menuItem.label)}

                    {menuItem.description && (
                      <$Description>
                        {SafeFormattedMessage(menuItem.description)}
                      </$Description>
                    )}
                  </$MenuTextContainer>
                </$MenuContent>
              </$MenuItem>

              {!!menuItem.SecondaryAction && menuItem.SecondaryAction}
            </$MenuButtonContainer>
          );
        } else {
          component = (
            <$MenuButtonContainer
              $isNewUiEnabled={isNewUiEnabled}
              $color={menuItem.color}
              {...dimmerAnimation}
            >
              <$MenuButton
                onClick={(e) => {
                  const response = { ...menuItem };

                  if (menuItem.onClick) {
                    menuItem.onClick(response, e);
                  }
                }}
                type={BUTTON_TYPES.BUTTON}
                ref={menuItem.ref}
                data-testid={menuItem.dataTestId || 'menu_child_button'}
                disabled={menuItem.isDisabled}
                $color={menuItem.color}
                $isNewUiEnabled={isNewUiEnabled}
                $hasSecondaryAction={!!menuItem.SecondaryAction}
                $minWidth={minWidth}
              >
                {menuItem.isLoading && (
                  <LoaderIcon
                    size="32px"
                    color={
                      isNewUiEnabled
                        ? theme.colors.mainTextColor
                        : theme.colors.white
                    }
                  />
                )}
                {!menuItem.isLoading && menuItem.icon}

                <$MenuContent>
                  <$MenuTextContainer>
                    {SafeFormattedMessage(menuItem.label)}
                    {menuItem.description && (
                      <$Description>
                        {SafeFormattedMessage(menuItem.description)}
                      </$Description>
                    )}
                  </$MenuTextContainer>
                </$MenuContent>
              </$MenuButton>

              {!!menuItem.SecondaryAction && menuItem.SecondaryAction}
            </$MenuButtonContainer>
          );
        }
      }
    }

    return (
      <$MenuItemStyled
        key={menuItem.key}
        data-testid={`menu_${menuItem.key}`}
        $minWidth={minWidth}
      >
        {component && React.cloneElement(component)}
      </$MenuItemStyled>
    );
  });
};

export default VerticalMenuContent;
